import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import QuotaBadge from "../components/QuotaBadge";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import SpeakButton from "../components/SpeakButton";
import { api, formatError } from "../lib/api";
import { API } from "../lib/api";
import axios from "axios";
import { Camera, Upload, ScanLine, RotateCcw, ShieldCheck } from "lucide-react";

export default function Decoder() {
  const { user, refresh } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setResult("");
    setPreview(URL.createObjectURL(file));
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const token = localStorage.getItem("sereno_token");
      const { data } = await axios.post(`${API}/decode/photo`, form, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setResult(data.explanation);
      refresh();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
      if (e.target) e.target.value = "";
    }
  };

  const reset = () => {
    setPreview("");
    setResult("");
    setError("");
  };

  if (!user) return null;

  return (
    <div className="mx-auto max-w-3xl px-6 py-10" data-testid="decoder-page">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#101A42]/10 text-[#101A42]">
          <ScanLine className="h-7 w-7" strokeWidth={2} />
        </div>
        <div>
          <p className="text-sm font-semibold uppercase tracking-widest text-[#101A42]">
            {t("decoder")}
          </p>
          <h1 className="font-serif text-3xl font-semibold text-[#101A42] sm:text-4xl">
            {t("decoder_title")}
          </h1>
        </div>
      </div>
      <p className="text-lg leading-relaxed text-[#333]">{t("decoder_intro")}</p>

      <div className="mt-6">
        <QuotaBadge kind="decode" limit={3} usage={user?.usage} lang={lang} testid="decode-quota" />
      </div>

      {!preview && !result && (
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <label
            data-testid="decoder-take-btn"
            className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-[#A762B1]/40 bg-white p-8 text-center hover:border-[#A762B1] hover:bg-[#A762B1]/5"
          >
            <Camera className="h-10 w-10 text-[#A762B1]" strokeWidth={2} />
            <span className="font-serif text-xl font-semibold text-[#101A42]">
              {t("decoder_take")}
            </span>
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onFile}
              className="hidden"
              data-testid="decoder-camera-input"
            />
          </label>
          <label
            data-testid="decoder-choose-btn"
            className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-[#101A42]/30 bg-white p-8 text-center hover:border-[#101A42] hover:bg-[#101A42]/5"
          >
            <Upload className="h-10 w-10 text-[#101A42]" strokeWidth={2} />
            <span className="font-serif text-xl font-semibold text-[#101A42]">
              {t("decoder_choose")}
            </span>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onFile}
              className="hidden"
              data-testid="decoder-file-input"
            />
          </label>
        </div>
      )}

      {preview && (
        <div className="mt-8 grid gap-6 md:grid-cols-2" data-testid="decoder-result-block">
          <img
            src={preview}
            alt="preview"
            className="w-full rounded-2xl border-2 border-[#E8DFC7] object-cover"
            data-testid="decoder-preview"
          />
          <div className="rounded-2xl border-2 border-[#101A42] bg-[#F8EFD9] p-5">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-semibold uppercase tracking-widest text-[#A762B1]">
                {t("decoder_result")}
              </p>
              {result && <SpeakButton text={result} testid="decoder-speak" />}
            </div>
            {busy ? (
              <p data-testid="decoder-analyzing" className="text-lg italic text-[#4A4A4A]">
                {t("decoder_analyzing")}
              </p>
            ) : error ? (
              <p data-testid="decoder-error" className="text-lg text-[#B91C1C]">
                {error}
              </p>
            ) : result ? (
              <div
                data-testid="decoder-explanation"
                className="whitespace-pre-line text-base leading-relaxed text-[#101A42]"
              >
                {result}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {(result || error) && (
        <div className="mt-6 flex justify-center">
          <Button
            onClick={reset}
            data-testid="decoder-reset-btn"
            className="min-h-[56px] rounded-full bg-[#101A42] px-6 text-lg font-semibold text-white hover:bg-[#1A2650]"
          >
            <RotateCcw className="mr-2 h-5 w-5" />
            {t("decoder_new")}
          </Button>
        </div>
      )}

      <div className="mt-8 flex items-start gap-3 rounded-2xl border border-[#A762B1]/30 bg-[#A762B1]/5 p-4">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#A762B1]" />
        <div>
          <p className="text-base text-[#101A42]">{t("decoder_privacy")}</p>
          <p className="mt-1 text-sm text-[#4A4A4A]">{t("decoder_tip_lighting")}</p>
        </div>
      </div>
    </div>
  );
}
